import { createScrollManager } from '@/utils/scrollUtils';
import { nextTick } from 'vue';

export default {
    data() {
        return {
            scrollManager: null,
            showScrollLeft: false,
            showScrollRight: false,

            // Плавающие кнопки прокрутки
            buttonOffsetTop: 0,
            buttonHeight: 100,

            // Sticky header
            stickyVisible: false,
            stickyLeft: 0,
            stickyWidth: 0,
            stickyTableWidth: 0,
            columnWidths: [],
            stickyTheadHtml: '',
            stickyOffsetTop: 0,
            scrollLeft: 0,
        };
    },

    mounted() {
        // Слушатели окна можно вешать сразу — они не зависят от наличия рефа
        window.addEventListener('scroll', this.onWindowScrollButtons, { passive: true });
        window.addEventListener('resize', this.updateButtonsPosition);
        window.addEventListener('scroll', this.onWindowScrollSticky, { passive: true });
        window.addEventListener('resize', this.onResizeSticky);

        // Ждём, пока ref="tableScroll" реально появится в DOM
        this._waitForScrollerAndInit();
    },

    beforeUnmount() {
        this._scrollerInited = false;

        if (this._scrollerObserver) {
            this._scrollerObserver.disconnect();
            this._scrollerObserver = null;
        }

        this.destroyScrollManager();

        window.removeEventListener('scroll', this.onWindowScrollButtons);
        window.removeEventListener('resize', this.updateButtonsPosition);
        window.removeEventListener('scroll', this.onWindowScrollSticky);
        window.removeEventListener('resize', this.onResizeSticky);

        const scroller = this.$refs.tableScroll;
        if (scroller) scroller.removeEventListener('scroll', this.onTableScrollSync);

        if (this._btnsRaf) {
            cancelAnimationFrame(this._btnsRaf);
            this._btnsRaf = null;
        }
        if (this._stickyRaf) {
            cancelAnimationFrame(this._stickyRaf);
            this._stickyRaf = null;
        }
    },

    computed: {
        floatingButtonStyle() {
            return {
                position: 'absolute',
                top: `${this.buttonOffsetTop}px`,
            };
        },
        stickyHeaderStyle() {
            return {
                position: 'fixed',
                top: `${this.stickyOffsetTop}px`,
                left: `${this.stickyLeft}px`,
                width: `${this.stickyWidth}px`,
            };
        },
    },

    methods: {
        /**
         * Универсальный «ожидатель» рефа: пытается инициализировать сразу,
         * иначе подписывается на изменения DOM через MutationObserver.
         */
        _waitForScrollerAndInit() {
            nextTick(() => {
                if (this._tryInitScrollFeatures()) return;

                // Реф ещё не появился — следим за DOM текущего компонента
                const root = this.$el && this.$el.nodeType === 1
                    ? this.$el
                    : document.body;

                this._scrollerObserver = new MutationObserver(() => {
                    if (this._tryInitScrollFeatures() && this._scrollerObserver) {
                        this._scrollerObserver.disconnect();
                        this._scrollerObserver = null;
                    }
                });

                this._scrollerObserver.observe(root.parentNode || document.body, {
                    childList: true,
                    subtree: true,
                });
            });
        },

        _tryInitScrollFeatures() {
            if (this._scrollerInited) return true;
            if (!this.$refs.tableScroll) return false;

            this._scrollerInited = true;
            this.initScrollManager();
            this.updateButtonsPosition();
            this.initStickyHeader();
            return true;
        },

        initScrollManager(refName = 'tableScroll', step = 150) {
            this.scrollManager = createScrollManager(
                () => this.$refs[refName],
                step,
                (left, right) => {
                    this.showScrollLeft = left;
                    this.showScrollRight = right;
                }
            );

            nextTick(() => {
                this.scrollManager.updateScrollButtons();
                window.addEventListener('resize', this.scrollManager.updateScrollButtons);
            });
        },

        destroyScrollManager() {
            if (this.scrollManager) {
                window.removeEventListener('resize', this.scrollManager.updateScrollButtons);
                this.scrollManager.destroy();
                this.scrollManager = null;
            }
        },

        updateScrollButtons() {
            this.scrollManager?.updateScrollButtons();
        },

        startAutoScroll(direction) {
            this.scrollManager?.startAutoScroll(direction);
        },

        stopAutoScroll() {
            this.scrollManager?.stopAutoScroll();
        },

        updateScrollOnDataChange() {
            nextTick(() => {
                // Если реф ещё не появился — попробуем ещё раз
                if (!this._scrollerInited) {
                    this._tryInitScrollFeatures();
                }
                this.scrollManager?.updateScrollButtons();
                this.syncStickyHeader();
                this.updateStickyHeader();
            });
        },

        // ===== Плавающие кнопки =====
        onWindowScrollButtons() {
            if (this._btnsRaf) return;
            this._btnsRaf = requestAnimationFrame(() => {
                this._btnsRaf = null;
                this.updateButtonsPosition();
            });
        },

        updateButtonsPosition() {
            const container = this.$refs.tableGroup;
            if (!container) return;

            const rect = container.getBoundingClientRect();
            const viewportH = window.innerHeight;

            const desiredViewportY = viewportH / 2 - this.buttonHeight / 2;

            let offset = desiredViewportY - rect.top;

            const minOffset = 0;
            const maxOffset = container.clientHeight - this.buttonHeight;

            offset = Math.max(minOffset, Math.min(offset, maxOffset));

            if (offset < 180) offset = 180;

            this.buttonOffsetTop = offset;
        },

        // ===== Sticky header =====
        initStickyHeader() {
            nextTick(() => {
                this.syncStickyHeader();
                const scroller = this.$refs.tableScroll;
                if (scroller) {
                    scroller.addEventListener('scroll', this.onTableScrollSync, { passive: true });
                }
                this.updateStickyHeader();
            });
        },

        onWindowScrollSticky() {
            if (this._stickyRaf) return;
            this._stickyRaf = requestAnimationFrame(() => {
                this._stickyRaf = null;
                this.updateStickyHeader();
            });
        },

        syncStickyHeader() {
            const scroller = this.$refs.tableScroll;
            if (!scroller) return;
            const table = scroller.querySelector('table');
            if (!table) return;
            const thead = table.querySelector('thead');
            if (!thead) return;

            const measureRow =
                table.querySelector('tbody tr') || thead.querySelector('tr');
            if (measureRow) {
                this.columnWidths = Array.from(measureRow.children).map(
                    cell => cell.getBoundingClientRect().width
                );
            }

            this.stickyTableWidth = table.getBoundingClientRect().width;
            this._theadHeight = thead.getBoundingClientRect().height;
        },

        onResizeSticky() {
            this.syncStickyHeader();
            this.updateStickyHeader();
        },

        updateStickyHeader() {
            const scroller = this.$refs.tableScroll;
            if (!scroller) return;
            const table = scroller.querySelector('table');
            if (!table) return;

            const tableRect = table.getBoundingClientRect();
            const top = this.stickyOffsetTop;
            const theadHeight = this._theadHeight || 0;

            const shouldShow =
                tableRect.top < top &&
                tableRect.bottom > top + theadHeight;

            if (shouldShow) {
                if (!this.columnWidths.length) this.syncStickyHeader();

                const scrollerRect = scroller.getBoundingClientRect();
                if (this.stickyLeft !== scrollerRect.left) this.stickyLeft = scrollerRect.left;
                if (this.stickyWidth !== scrollerRect.width) this.stickyWidth = scrollerRect.width;
                if (!this.stickyVisible) this.stickyVisible = true;

                this.scrollLeft = scroller.scrollLeft;
            } else if (this.stickyVisible) {
                this.stickyVisible = false;
            }
        },

        onTableScrollSync() {
            if (!this.stickyVisible || this._syncRaf) return;
            this._syncRaf = requestAnimationFrame(() => {
                this._syncRaf = null;
                const scroller = this.$refs.tableScroll;
                if (scroller) this.scrollLeft = scroller.scrollLeft;
            });
        },
    },
};